// src/components/visitors/ConvertVisitorModal.js
import React, { useState } from 'react';
import axios from 'axios';
import { X, Check, UserCheck } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const ConvertVisitorModal = ({ isOpen, selectedVisitor, owners, onClose, onSuccess }) => {
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [converting, setConverting] = useState(false);

  if (!isOpen || !selectedVisitor) return null;

  const countMovements = (movements, type) => {
    if (!movements) return 0;
    return movements.filter(m => m.type === type).length;
  };

  const handleConvert = async () => {
    if (!selectedVisitor?.aircraft_id || !selectedOwnerId) return;
    setConverting(true);
    try {
      // Utilise le endpoint assign-owner dédié
      await axios.post(
        `${API_BASE}/aircraft/${selectedVisitor.aircraft_id}/assign-owner`,
        null,
        { params: { owner_id: selectedOwnerId } }
      );
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error assigning owner:', err);
      alert('Error during conversion: ' + (err.response?.data?.detail || err.message));
    } finally {
      setConverting(false);
      setSelectedOwnerId('');
    }
  };

  const localOwners = owners.filter(o => !o.owner_type || o.owner_type === 'local');
  const offsiteOwners = owners.filter(o => o.owner_type === 'offsite');

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-md">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <UserCheck size={18} className="text-green-400" />
            <h3 className="text-white font-semibold">Link Aircraft to Owner</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-gray-300 text-sm">
            Link <span className="font-mono font-bold text-white">{selectedVisitor.tail_number}</span> to an existing owner account.
          </p>

          <div>
            <label className="text-gray-400 text-sm block mb-1">Select Owner</label>
            <select
              value={selectedOwnerId}
              onChange={(e) => setSelectedOwnerId(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
            >
              <option value="">-- Select owner --</option>

              {localOwners.length > 0 && (
                <optgroup label="🏠 Local Clients">
                  {localOwners.map(owner => (
                    <option key={owner.id} value={owner.id}>
                      {owner.company_name || `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.email}
                    </option>
                  ))}
                </optgroup>
              )}

              {offsiteOwners.length > 0 && (
                <optgroup label="✈️ Offsite Owners">
                  {offsiteOwners.map(owner => (
                    <option key={owner.id} value={owner.id}>
                      {owner.company_name || `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.email}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
            <p className="text-blue-400 text-xs">
              ℹ️ This links the aircraft to the selected owner. All future movements will be billable to their account.
              Past movements ({countMovements(selectedVisitor.movements, 'TAKEOFF')} takeoffs,{' '}
              {countMovements(selectedVisitor.movements, 'LANDING')} landings) are not retroactively billed.
            </p>
          </div>
        </div>

        <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleConvert}
            disabled={!selectedOwnerId || converting}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-lg text-white text-sm flex items-center gap-2"
          >
            {converting
              ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              : <Check size={16} />}
            Link Owner
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConvertVisitorModal;